import Checkbox from '@veeva/corgix/checkbox';
import Tooltip from '@veeva/corgix/tooltip';
import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@veeva/corgix/button';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import AppContext from '../../../common/containers/AppContext';
import ApiError, { ApiErrorType } from '../../../common/services/api/ApiError';
import addLastFiltered from '../../services/data/addLastFiltered';
import addSavedFilter from '../../services/data/addSavedFilter';
import useGetApplicationsWithStatsByFamilyId from '../../services/data/useGetApplicationsWithStatsByFamilyId';
import doesFilterNameExist from '../../services/doesFilterNameExist';
import { AppStatsQAPlatformKey, VaultPlatformKey } from '../../services/FamilyConstants';
import getAllStatsForFilter from '../../services/getAllStatsForFilter';
import getVaultBasicsFilterOption from '../../services/getVaultBasicsFilterOption';
import { showInvalidFilterNameNotification } from '../../../common/services/notifications';
import VaultBasicsFilterOptions from '../../services/VaultBasicsFilterOptions';
import pageIdentifiersPageType from '../PageIdentifiersPageType';
import CustomerStatFilterSavedFiltersSelection from './CustomerStatFilterSavedFiltersSelection';
import FilterSelectionDropdownMenuContainer from './FilterSelectionDropdownMenuContainer';
import { customerStatFilterByApplicationSeeMoreCSS } from './styles/CustomerStatFilter.styles';
import {
    customerStatFilterCloseOverlayButtonCSS,
    customerStatFilterHeaderMobileCSS,
    customerStatFilterHeaderTextMobileCSS,
    customerStatFilterMobileCancelApplyContainerCSS,
    customerStatFilterMobileCSS,
    customerStatFilterMobileFooterCSS,
} from './styles/CustomerStatFilterMobile.styles';
import OverlayContext from '../../../common/containers/OverlayContext';
import IconButtonWithRef from '../../../common/components/IconButton';
import useGetFamilyById from '../../services/data/useGetFamilyById';
import { customerStatFilterDialogApplySaveFilterButtonCSS } from './styles/CustomerStatFilterDialogButtons.styles';
import CustomerStatFilterContext from './CustomerStatFilterContext';
import pageGroupType from '../PageGroupType';
import modifyErrorMessage from '../../services/modifyErrorMessage';

export default function CustomerStatFilterMobile() {
    const { familyId } = useParams();
    const [family] = useGetFamilyById(Number(familyId));
    const { setOverlay } = useContext(OverlayContext);
    const [applicationsWithAvailableStats] = useGetApplicationsWithStatsByFamilyId(
        Number(familyId),
    );
    const { customers, families } = useContext(AppContext);
    const availableCustomers = customers.map(({ id, label }) => {
        return { id: id.toString(), label };
    });

    const nonPlatformFamilyApplications = applicationsWithAvailableStats?.filter(
        (application) =>
            application.key !== family?.key &&
            application.label !== family?.label &&
            application.key !== VaultPlatformKey &&
            application.key !== AppStatsQAPlatformKey,
    );

    const {
        selectedCustomerFilterList,
        setSelectedCustomerFilterList,
        setAppliedCustomerFilterList,
        pendingFilterId,
        setSelectedFilterId,
        userPreferenceFilters,
        seeOnlyFilterApp,
        pendingFilterList,
        setSelectedFilterList,
        pendingFilterQuery,
        setSelectedFilterQuery,
        setResetFilters,
        pendingFilterName,
        setUserPreferenceFilters,
        setPendingFilterId,
        setPendingFilterName,
        setPendingCreateCustomFilterList,
        setPendingCreateCustomFilterQuery,
        setCreateCustomFilterName,
        pageGroup,
        setErrorMsg,
    } = useContext(CustomerStatFilterContext);

    let allStats;
    if (pageGroup === pageGroupType.ALL_CUSTOMERS) {
        allStats = ['Applications', 'Application Families'];
    } else {
        allStats = getAllStatsForFilter(families, family);
    }
    const handleClose = () => {
        setOverlay(null);
    };

    const handleSaveLastFiltered = async () => {
        let errorLogs;
        if (pageGroup === pageGroupType.ALL_CUSTOMERS) {
            errorLogs = await addLastFiltered(
                {
                    pageGroup,
                    pageType: pageIdentifiersPageType.CUSTOMERS,
                },
                {
                    filter_query: pendingFilterQuery,
                    filter_id: pendingFilterId,
                    filters_list: pendingFilterList.map((filter) => {
                        return filter;
                    }),
                },
                false,
            );
        } else {
            errorLogs = await addLastFiltered(
                {
                    pageGroup,
                    pageType: pageIdentifiersPageType.TOTALS,
                    familyId,
                },
                {
                    filter_query: pendingFilterQuery,
                    see_only_apps: seeOnlyFilterApp.pending,
                    filter_id: pendingFilterId,
                    filters_list: pendingFilterList.map((filter) => {
                        if (filter.vaultBasics) {
                            if (filter.vaultBasics === VaultBasicsFilterOptions.Yes) {
                                return { vaultBasics: true };
                            }
                            if (filter.vaultBasics === VaultBasicsFilterOptions.No) {
                                return { vaultBasics: false };
                            }
                            return { vaultBasics: null };
                        }
                        return filter;
                    }),
                },
                false,
            );
        }

        if (errorLogs?.errors?.length > 0) {
            const errorMsg = errorLogs.errors[0]?.message;
            setErrorMsg(modifyErrorMessage(errorMsg));
            throw new ApiError(ApiErrorType.VALIDATION_ERROR);
        }
    };

    const handleApply = async () => {
        setOverlay(null);
        await handleSaveLastFiltered();
        setAppliedCustomerFilterList(selectedCustomerFilterList);
        setSelectedFilterId(pendingFilterId);
        seeOnlyFilterApp.setSelected(seeOnlyFilterApp.pending);
        setSelectedFilterList(pendingFilterList);
        setSelectedFilterQuery(pendingFilterQuery);

        setResetFilters(false);
    };

    const handleSave = async () => {
        let filterName = selectedCustomerFilterList;
        if (pendingFilterName !== selectedCustomerFilterList) {
            filterName = pendingFilterName;
        }
        if (pendingFilterList.length !== 0 && pendingFilterQuery.length !== 0) {
            if (doesFilterNameExist(userPreferenceFilters, filterName, pendingFilterId)) {
                showInvalidFilterNameNotification(filterName);
                return;
            }
            let newFilter;
            if (pageGroup === pageGroupType.ALL_CUSTOMERS) {
                newFilter = await addSavedFilter(
                    {
                        pageGroup,
                        pageType: pageIdentifiersPageType.CUSTOMERS,
                    },
                    {
                        filter_query: pendingFilterQuery,
                        id: pendingFilterId,
                        filters_list: pendingFilterList.map((filter) => {
                            return filter;
                        }),
                        filter_name: filterName,
                    },
                );
            } else {
                newFilter = await addSavedFilter(
                    {
                        pageGroup,
                        pageType: pageIdentifiersPageType.TOTALS,
                        familyId,
                    },
                    {
                        filter_query: pendingFilterQuery,
                        see_only_apps: seeOnlyFilterApp.pending,
                        id: pendingFilterId,
                        filters_list: pendingFilterList.map((filter) => {
                            if (filter.vaultBasics) {
                                if (filter.vaultBasics === VaultBasicsFilterOptions.Yes) {
                                    return { vaultBasics: true };
                                }
                                if (filter.vaultBasics === VaultBasicsFilterOptions.No) {
                                    return { vaultBasics: false };
                                }
                                return { vaultBasics: null };
                            }
                            return filter;
                        }),
                        filter_name: filterName,
                    },
                );
            }

            if (selectedCustomerFilterList === 'create_custom') {
                setPendingCreateCustomFilterList([]);
                setPendingCreateCustomFilterQuery('');
                setCreateCustomFilterName('');
            }
            setPendingFilterName(newFilter?.filter_name);
            setSelectedCustomerFilterList(newFilter?.filter_name);
            setPendingFilterId(newFilter?.id);

            const newUserPreferences = userPreferenceFilters.filter(
                (f) => f.id !== pendingFilterId,
            );

            if (pageGroup === pageGroupType.ALL_CUSTOMERS) {
                newUserPreferences.push({
                    id: newFilter?.id,
                    filterQuery: newFilter?.filter_query,
                    filterName: newFilter?.filter_name,
                    customerFiltersList: newFilter?.filters_list.map((filter) => {
                        return {
                            filterType: filter?.filterType,
                            applications: filter?.applications,
                            appFamilyKeys: filter?.appFamilyKeys,
                        };
                    }),
                });
            } else {
                newUserPreferences.push({
                    id: newFilter?.id,
                    filterQuery: newFilter?.filter_query,
                    filterName: newFilter?.filter_name,
                    filterList: newFilter?.filters_list.map((filter) => {
                        return {
                            customerIds: filter?.customerIds,
                            vaultBasics: getVaultBasicsFilterOption(filter),
                            vaultSizes: filter?.vaultSizes,
                            podNames: filter?.podNames,
                            appKeys: filter?.appKeys,
                            statFilter: filter?.statFilter,
                            customerSizes: filter?.customerSizes,
                            industries: filter?.industries,
                            regions: filter?.regions,
                            segments: filter?.segments,
                            additionalInfo: filter?.additionalInfo,
                        };
                    }),
                });
            }
            setUserPreferenceFilters(newUserPreferences);
        }
    };

    const handleSeeApplicationsChecked = (e, isChecked) => {
        seeOnlyFilterApp.setPending(isChecked);
    };

    const toolTipContent = () => {
        if (userPreferenceFilters?.length >= 5 && selectedCustomerFilterList === 'create_custom') {
            return <div>Max customer filters reached.</div>;
        }
        if (selectedCustomerFilterList === 'redReport') {
            return <div>Red Report cannot be saved.</div>;
        }
        return '';
    };

    const isDisabled = pendingFilterList?.length === 0 || pendingFilterName?.length === 0;

    return (
        <div css={customerStatFilterMobileCSS}>
            <div css={customerStatFilterHeaderMobileCSS}>
                <div css={customerStatFilterHeaderTextMobileCSS}>Filters</div>
                <IconButtonWithRef
                    icon={faXmark}
                    title="Close"
                    css={customerStatFilterCloseOverlayButtonCSS}
                    iconSize="md"
                    onClick={handleClose}
                />
            </div>
            <CustomerStatFilterSavedFiltersSelection
                allStats={allStats}
                family={family}
                mobile
                showPlatformOnlyFilters={family?.key === 'platform__v'}
            />
            <FilterSelectionDropdownMenuContainer
                availableCustomers={availableCustomers}
                applicationsWithAvailableStats={nonPlatformFamilyApplications}
                platformKey={family?.platform_family_key}
                mobile
                allStats={allStats}
            />
            <div css={customerStatFilterMobileFooterCSS}>
                <div>
                    {pendingFilterList &&
                    pendingFilterList.some((filter) => filter.appKeys && filter.appKeys !== 0) ? (
                        <span css={customerStatFilterByApplicationSeeMoreCSS}>
                            <Checkbox
                                label={<span>See selected applications only</span>}
                                onChange={handleSeeApplicationsChecked}
                                checked={seeOnlyFilterApp.pending}
                            />
                        </span>
                    ) : null}
                </div>
                <div css={customerStatFilterMobileCancelApplyContainerCSS}>
                    <Button variant="ghost" onClick={handleClose}>
                        Cancel
                    </Button>

                    {toolTipContent() === '' ? (
                        <Button
                            variant="secondary"
                            onClick={handleSave}
                            css={customerStatFilterDialogApplySaveFilterButtonCSS}
                            disabled={
                                isDisabled ||
                                userPreferenceFilters?.length >= 5 ||
                                selectedCustomerFilterList === 'redReport'
                            }
                        >
                            Save
                        </Button>
                    ) : (
                        <Tooltip content={toolTipContent()} placement="top">
                            <span>
                                <Button
                                    variant="secondary"
                                    onClick={handleSave}
                                    css={customerStatFilterDialogApplySaveFilterButtonCSS}
                                    disabled={
                                        isDisabled ||
                                        userPreferenceFilters?.length >= 5 ||
                                        selectedCustomerFilterList === 'redReport'
                                    }
                                >
                                    Save
                                </Button>
                            </span>
                        </Tooltip>
                    )}

                    <Button variant="primary" onClick={handleApply}>
                        Apply
                    </Button>
                </div>
            </div>
        </div>
    );
}
